// src/pages/employeeCommission/components/ProductLevelBulkModal.jsx
import React, { useState, useRef } from 'react';
import { X, Plus, Trash2, Search } from 'lucide-react';
import { getProducts } from '../../../services/api/productSearch';
import { bulkUpsertCommissionConfigs } from '../../../services/api/employeeCommission';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './PageModals.module.css';

const EMPTY_ROW = () => ({
  productId: '',
  productLabel: '',
  confirmCommission: '',
  createCommission: '',
  calculationBasis: 'order_count',
});

const ProductLevelBulkModal = ({ onClose, onSaved, existingConfigs = [] }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  const isEdit = existingConfigs.length > 0;
  const [rows, setRows] = useState(
    isEdit
      ? existingConfigs.map((c) => ({
          productId: c.productId,
          productLabel: c.product?.name || '',
          confirmCommission: c.confirmCommission,
          createCommission: c.createCommission,
          calculationBasis: c.calculationBasis,
        }))
      : [EMPTY_ROW()]
  );
  const [productSearches, setProductSearches] = useState({});
  const [productOptions, setProductOptions] = useState({});
  const [saving, setSaving] = useState(false);
  const timers = useRef({});

  const handleProductSearch = async (idx, val) => {
    setProductSearches((prev) => ({ ...prev, [idx]: val }));
    if (val.length < 2) return;
    clearTimeout(timers.current[idx]);
    timers.current[idx] = setTimeout(async () => {
      try {
        const res = await getProducts({ search: val, limit: 30 });
        setProductOptions((prev) => ({ ...prev, [idx]: res?.products || [] }));
      } catch { }
    }, 400);
  };

  const addRow = () => setRows([...rows, EMPTY_ROW()]);
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  const updateRow = (idx, field, value) =>
    setRows(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const basisLabel = (key) => {
    const map = {
      order_count: t('employeeCommission.orderCount'),
      order_quantity: t('employeeCommission.orderQuantity'),
      order_value: t('employeeCommission.orderValue'),
    };
    return map[key] || key;
  };

  const handleSave = async () => {
    const valid = rows.every(
      (r) => r.productId && r.confirmCommission !== '' && r.createCommission !== ''
    );
    if (!valid) { showError('Please fill all fields.'); return; }

    setSaving(true);
    try {
      const configs = rows.map((r) => ({
        userId: null,
        productId: parseInt(r.productId),
        confirmCommission: parseFloat(r.confirmCommission),
        createCommission: parseFloat(r.createCommission),
        calculationBasis: r.calculationBasis,
      }));
      await bulkUpsertCommissionConfigs(configs);
      success(t('employeeCommission.bulkConfigSaved'));
      onSaved();
      onClose();
    } catch (err) {
      showError(err.response?.data?.message || t('employeeCommission.configSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.modalLarge}`}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalTitle}>{t('employeeCommission.bulkProductConfig')}</h2>
          </div>
          <button className={styles.closeButton} onClick={onClose}><X size={16} /></button>
        </div>

        <div className={styles.modalBody}>
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.bulkTable}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Basis</th>
                  <th>Confirm</th>
                  <th>Create</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ minWidth: 220 }}>
                      {!isEdit ? (
                        <>
                          <div className={styles.searchWrapper} style={{ marginBottom: '0.25rem' }}>
                            <Search size={14} className={styles.searchIcon} style={{ top: '50%' }} />
                            <input
                              className={styles.searchInput}
                              style={{ padding: '0.4rem 0.75rem 0.4rem 2.25rem', fontSize: '0.8125rem' }}
                              placeholder="Search product..."
                              value={productSearches[idx] || ''}
                              onChange={(e) => handleProductSearch(idx, e.target.value)}
                            />
                          </div>
                          {productOptions[idx]?.length > 0 && (
                            <select
                              value={row.productId}
                              onChange={(e) => {
                                const prod = productOptions[idx].find(
                                  (p) => String(p.productId || p.id) === e.target.value
                                );
                                updateRow(idx, 'productId', e.target.value);
                                // updateRow(idx, 'productLabel', prod?.displayName || prod?.name || '');
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">-- Select --</option>
                              {productOptions[idx].map((p) => (
                                <option key={p.productId || p.id} value={p.productId || p.id}>
                                  {p.displayName || p.name}
                                </option>
                              ))}
                            </select>
                          )}
                          {row.productLabel && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--color-success-dark)' }}>
                              ✓ {row.productLabel}
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={{ fontSize: '0.875rem' }}>{row.productLabel}</span>
                      )}
                    </td>
                    <td>
                      <select
                        value={row.calculationBasis}
                        onChange={(e) => updateRow(idx, 'calculationBasis', e.target.value)}
                        style={{ minWidth: 130 }}
                      >
                        {['order_count', 'order_quantity', 'order_value'].map((v) => (
                          <option key={v} value={v}>{basisLabel(v)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number" min="0" step="0.01"
                        value={row.confirmCommission}
                        onChange={(e) => updateRow(idx, 'confirmCommission', e.target.value)}
                        placeholder="0.00" style={{ width: 100 }}
                      />
                    </td>
                    <td>
                      <input
                        type="number" min="0" step="0.01"
                        value={row.createCommission}
                        onChange={(e) => updateRow(idx, 'createCommission', e.target.value)}
                        placeholder="0.00" style={{ width: 100 }}
                      />
                    </td>
                    <td>
                      <button
                        className={styles.btnDanger}
                        style={{ padding: '0.375rem' }}
                        onClick={() => removeRow(idx)}
                        disabled={rows.length === 1}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isEdit && (
            <button className={styles.btnSecondary} onClick={addRow} style={{ marginTop: '1rem' }}>
              <Plus size={16} /> Add Row
            </button>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>
            {t('employeeCommission.cancel')}
          </button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? <><div className={styles.spinner} />{t('employeeCommission.saving')}</> : t('employeeCommission.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductLevelBulkModal;